import React, { useState, useRef } from "react";
import _ from "lodash";

export const TagsInputs = React.forwardRef(
    (
        {
            label,
            id,
            name,
            placeholder = "Type and press Enter...",
            disabled = false,
            required = false,
            isHideLabel = false,
            className = "",
            subTitle = "",
            subRequired = false,
            errors = null,
            errorName = "",
            value = [],
            onChange,
            maxTags = null,
            ...props
        },
        ref
    ) => {
        const [inputValue, setInputValue] = useState("");
        const inputRef = useRef(null);
        const msg = _.get(errors || {}, errorName || name);

        // expose the input ref
        React.useImperativeHandle(ref, () => inputRef.current);

        const tags = Array.isArray(value) ? value : [];

        const addTag = () => {
            const trimmedValue = inputValue.trim();
            if (!trimmedValue) return;

            // avoid duplicates
            if (tags.includes(trimmedValue)) {
                setInputValue("");
                return;
            }

            if (maxTags && tags.length >= maxTags) return;

            const newTags = [...tags, trimmedValue];
            onChange?.(newTags); // parent owns the state
            setInputValue("");
        };

        const removeTag = (indexToRemove) => {
            const newTags = tags.filter((_, index) => index !== indexToRemove);
            onChange?.(newTags);
        };

        const handleKeyDown = (e) => {
            if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addTag();
            } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
                removeTag(tags.length - 1);
            }
        };

        return (
            <div className="font-urbanist">
                {(!!label || !isHideLabel) && (
                    <label
                        htmlFor={id}
                        className={`block text-lg font-semibold mb-1 ${disabled ? "text-gray-500" : "text-white"
                            }`}
                    >
                        {label} {required && <span className="text-red-600">*</span>}
                    </label>
                )}

                {!!subTitle && (
                    <p className="text-xs text-gray-400 mb-1">
                        {subTitle}
                        {subRequired && <span className="text-red-600">*</span>}
                    </p>
                )}

                <div
                    className={`bg-[#22262E] border border-gray-700 rounded-lg w-full p-2 flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-white ${disabled ? "cursor-not-allowed opacity-60" : ""
                        } ${className}`}
                    onClick={() => !disabled && inputRef.current?.focus()}
                >
                    {tags.map((tag, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center gap-1 bg-gray-700 text-gray-200 px-2 py-1 rounded-md text-sm"
                        >
                            {tag}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeTag(index);
                                    }}
                                    className="hover:bg-gray-600 rounded-full p-0.5 transition-colors"
                                >
                                    <svg
                                        stroke="#ffffff"
                                        fill="#ffffff"
                                        strokeWidth="0"
                                        viewBox="0 0 1024 1024"
                                        fillRule="evenodd"
                                        height="14px"
                                        width="14px"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M799.855 166.312c.023.007.043.018.084.059l57.69 57.69c.041.041.052.06.059.084a.118.118 0 0 1 0 .069c-.007.023-.018.042-.059.083L569.926 512l287.703 287.703c.041.04.052.06.059.083a.118.118 0 0 1 0 .07c-.007.022-.018.042-.059.083l-57.69 57.69c-.041.041-.06.052-.084.059a.118.118 0 0 1-.069 0c-.023-.007-.042-.018-.083-.059L512 569.926 224.297 857.629c-.04.041-.06.052-.083.059a.118.118 0 0 1-.07 0c-.022-.007-.042-.018-.083-.059l-57.69-57.69c-.041-.041-.052-.06-.059-.084a.118.118 0 0 1 0-.069c.007-.023.018-.042.059-.083L454.073 512 166.371 224.297c-.041-.04-.052-.06-.059-.083a.118.118 0 0 1 0-.07c.007-.022.018-.042.059-.083l57.69-57.69c.041-.041.06-.052.084-.059a.118.118 0 0 1 .069 0c.023.007.042.018.083.059L512 454.073l287.703-287.702c.04-.041.06-.052.083-.059a.118.118 0 0 1 .07 0Z" />
                                    </svg>
                                </button>
                            )}
                        </span>
                    ))}

                    <input
                        ref={inputRef}
                        id={id}
                        name={name}
                        type="text"
                        placeholder={tags.length === 0 ? placeholder : ""}
                        disabled={disabled}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={addTag}
                        className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-400 placeholder:text-gray-500"
                        {...props}
                    />
                </div>

                <div className="text-gray-700 text-[0.7rem] pl-2">
                    Write and press Enter to add tags
                </div>

                {!!msg && (
                    <div className="text-red-700 text-sm md:text-md mt-2">{msg}</div>
                )}

                {maxTags && (
                    <p className="text-xs text-gray-500 mt-1">
                        {tags.length}/{maxTags} tags
                    </p>
                )}
            </div>
        );
    }
);
